// TODO: legacy ui components
import { RichTextEditorComponent } from 'app/components/shared/rich-text-editor/rich-text-editor.component';
import { RichTextEditorModule } from 'app/components/shared/rich-text-editor/rich-text-editor.module';

import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { I18nPluralPipe, NgForOf, NgIf } from '@angular/common';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatDividerModule } from '@angular/material/divider';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { ButtonDirective, ButtonSpacerDirective } from '../../directives/button.directive';
import { AuthorLineDirective, LoadingListDirective } from '../../directives/list.directive';
import { FormSubmissionComment } from 'app/models/entities/forms/form-submission-comment';
import { ViolationStatementComment } from 'app/models/entities/violation-statement-comment';
import { ModelTypeKeys } from 'app/enums/model-type-keys.enum';
import { FormSubmission } from 'app/models/entities/forms/form-submission';
import { ViolationStatement } from 'app/models/entities/violation-statement';
import { RequestFormService } from 'app/services/forms/request-form.service';
import { ViolationsService } from 'app/services/violations/violations.service';
import { GrowlerService } from 'app/services/growler.service';
import { LegfiDatePipeModule } from 'app/pipes/format/legfi-date.module';
import { MatDialogSizes } from 'app/enums/mat-dialog-sizes.enum';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { EmptyStateModule } from '../../empty-state/empty-state.module';
import { ConfirmActionV2DialogComponent, ConfirmActionV2DialogInput } from '../../dialogs/confirm-action-dialog/confirm-action-dialog.component';
import { FlexSpacerDirective } from '../../directives/flex-spacer.directive';
import { MemberAdminDialogComponent, MemberAdminDialogInput, MemberAdminDialogOutput } from '../../../memberships/shared/member-admin-dialog/member-admin-dialog.component';
import { PreventClickDirective } from '../../directives/prevent-click.directive';

@UntilDestroy()
@Component({
    selector: 'app-comments-card',
    templateUrl: './comments-card.component.html',
    standalone: true,
    host: { class: 'comments-card' },
    imports: [
        MatCardModule,
        MatButtonModule,
        MatProgressBarModule,
        MatDividerModule,
        MatListModule,
        ButtonDirective,
        NgIf,
        LoadingListDirective,
        NgForOf,
        MatMenuModule,
        MatIconModule,
        LegfiDatePipeModule,
        ReactiveFormsModule,
        RichTextEditorModule,
        MatSlideToggleModule,
        I18nPluralPipe,
        ButtonSpacerDirective,
        MatDialogModule,
        AuthorLineDirective,
        EmptyStateModule,
        FlexSpacerDirective,
        PreventClickDirective,
    ],
})
export class CommentsCardComponent implements OnInit, OnChanges
{
    @Input() modelType: ModelTypeKeys;
    @Input() model: FormSubmission | ViolationStatement;
    @Input() isSaving = false;
    @Input() isOwner = false;

    @Output() commentsUpdated: EventEmitter<FormSubmissionComment[] | ViolationStatementComment[]>
            = new EventEmitter<FormSubmissionComment[] | ViolationStatementComment[]>();

    @ViewChild('editorContainer') editorContainerRef: ElementRef;
    @ViewChild(RichTextEditorComponent) editorRef: RichTextEditorComponent;

    formGroup = this._fb.group({
        id: null,
        comment: this._fb.nonNullable.control('', Validators.required),
        sendNotification: false,
        recipients: this._fb.nonNullable.control([]),
    });
    loadingInitialData = true;
    refreshingData = false;
    addCommentEnabled = false;
    comments: FormSubmissionComment[] | ViolationStatementComment[];

    recipientsMapping: { [prop: string]: string; } = {
        '=0': 'No recipients',
        '=1': '1 recipient',
        'other': '# recipients',
    };

    constructor(
            private _requestFormService: RequestFormService,
            private _violationsService: ViolationsService,
            private _fb: FormBuilder,
            private _dialog: MatDialog,
            private _growler: GrowlerService,
    ) {
    }

    get hasSelectedRecipients() {
        return this.formGroup.get('recipients').value?.length > 0;
    }

    get isSubmissionNote() {
        return this.modelType === ModelTypeKeys.FORM_SUBMISSION;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes && changes.hasOwnProperty('model') && this.model) {
            this.refreshComments();
        }
    }

    ngOnInit() {
        this.formGroup.get('sendNotification').valueChanges.pipe(untilDestroyed(this)).subscribe({
            next: (value) => value ? this.showNotificationMemberSelector() : this.formGroup.get('recipients').reset([]),
        });
    }

    refreshComments() {
        let obs: Observable<FormSubmissionComment[] | ViolationStatementComment[]>;
        let organizationId: number;
        switch (this.modelType) {
            case ModelTypeKeys.FORM_SUBMISSION:
                organizationId = (this.model as FormSubmission).form.organizationId;
                obs = this._requestFormService.getComments(this.model.id, {organization_id: organizationId});
                break;
            case ModelTypeKeys.VIOLATION:
                organizationId = (this.model as ViolationStatement).organizationId;
                obs = this._violationsService.getComments(this.model.id, {organization_id: organizationId});
                break;
        }

        if (obs) {
            this.refreshingData = true;
            obs.pipe(
                    finalize(() => {
                        this.loadingInitialData = this.refreshingData = false;
                        this.formGroup.get('id').reset();
                    }),
                    untilDestroyed(this),
            ).subscribe({
                next: (res) => {
                    this.comments = res || [];

                    if (!this.loadingInitialData) {
                        this.commentsUpdated.emit(this.comments);
                    }
                },
                error: () => this._growler.oops('Unable to load comments'),
            });
        }
    }

    showNotificationMemberSelector(): void {
        const data: MemberAdminDialogInput = {
            title: 'Select Recipients',
            required: false,
            hideMissingEmail: true,
            selectedMembershipIds: this.formGroup.get('recipients').value,
            orgId: this.model.organizationId,
            unitId: this.model.unitId,
            confirmButton: {
                '=0': 'Select Recipients',
                '=1': 'Select 1 Recipient',
                other: 'Select # Recipients',
            },
            emptyStateLabel: 'No recipients so far...',
        };

        this._dialog.open(MemberAdminDialogComponent, {
            width: MatDialogSizes.MD,
            data,
            autoFocus: 'dialog',
            panelClass: ['v2-dialog', 'selector-layout'],
        }).afterClosed().subscribe((result: MemberAdminDialogOutput) => {
            if (result?.success) {
                if (result?.selectedMemberships.length > 0) {
                    this.formGroup.get('recipients').setValue(result.selectedMemberships.map((m) => m.id));
                } else {
                    this.formGroup.get('sendNotification').reset(false);
                }
            }
        });
    }

    addComment() {
        this.addCommentEnabled = true;
        setTimeout(() => {
            this.editorContainerRef.nativeElement.scrollIntoView();
            this.editorRef.focus();
        }, 800);
    }

    editComment(commentId: number) {
        let comment: FormSubmissionComment | ViolationStatementComment;
        switch (this.modelType) {
            case ModelTypeKeys.FORM_SUBMISSION:
                comment = (this.comments as FormSubmissionComment[]).find(c => c.id === commentId);
                break;
            case ModelTypeKeys.VIOLATION:
                comment = (this.comments as ViolationStatementComment[]).find(c => c.id === commentId);
                break;
        }

        this.formGroup.reset({
            id: comment.id,
            comment: comment.message,
            sendNotification: false,
            recipients: [],
        });

        this.addComment();
    }

    cancelEdit() {
        this.addCommentEnabled = false;

        this.formGroup.reset({
            id: null,
            comment: '',
            sendNotification: false,
            recipients: [],
        });
    }

    deleteComment(commentId: number) {
        let obs: Observable<Object>;
        let type = 'item';
        let organizationId: number;
        switch (this.modelType) {
            case ModelTypeKeys.FORM_SUBMISSION:
                organizationId = (this.model as FormSubmission).form.organizationId;
                obs = this._requestFormService.deleteComment(this.model.id, commentId, {organization_id: organizationId});
                type = 'submission';
                break;
            case ModelTypeKeys.VIOLATION:
                organizationId = (this.model as ViolationStatement).organizationId;
                obs = this._violationsService.deleteComment(this.model.id, commentId, {organization_id: organizationId});
                type = 'violation';
                break;
        }

        if (obs) {
            const data: ConfirmActionV2DialogInput = {
                title: `Delete comment for this ${type}?`,
                content: 'This action cannot be undone.',
                confirmButton: 'Yes, delete comment',
                cancelButton: 'No',
            };
            this._dialog.open(ConfirmActionV2DialogComponent, {
                panelClass: ['v2-dialog', 'confirmation-layout'],
                width: MatDialogSizes.SM,
                autoFocus: 'dialog',
                data,
            }).afterClosed().subscribe({
                next: (res) => {
                    if (res?.success) {
                        this.addCommentEnabled = false;
                        this.formGroup.get('id').setValue(commentId);
                        obs.pipe(untilDestroyed(this)).subscribe({
                            next: () => {
                                this._growler.success('Success', 'Comment deleted');
                                this.refreshComments();
                            },
                            error: () => {
                                this._growler.oops('Unable to delete comment');
                                this.formGroup.get('id').reset(null);
                            },
                        });
                    }
                },
            });
        }
    }

    publishComment(commentId?: number) {
        const request = {
            message: this.formGroup.get('comment').value,
            notifyAdmins: this.isOwner,
            recipientMemberIds: this.formGroup.get('recipients').value,
        };

        let obs: Observable<any>;
        switch (this.modelType) {
            case ModelTypeKeys.FORM_SUBMISSION:
                if (commentId) {
                    obs = this._requestFormService.updateComment(this.model.id, commentId, request);
                } else {
                    obs = this._requestFormService.createComment(this.model.id, request);
                }
                break;
            case ModelTypeKeys.VIOLATION:
                if (commentId) {
                    obs = this._violationsService.updateComment(this.model.id, commentId, request);
                } else {
                    obs = this._violationsService.createComment(this.model.id, request);
                }
                break;
        }

        if (obs) {
            this.isSaving = true;
            obs.pipe(
                    finalize(() => this.isSaving = false),
                    untilDestroyed(this),
            ).subscribe({
                next: () => {
                    this.formGroup.reset({
                        id: null,
                        comment: '',
                        sendNotification: false,
                        recipients: [],
                    });

                    this._growler.success('Success', commentId ? 'Comment updated' : 'Comment published');
                    this.refreshComments();
                },
                error: () => this._growler.oops('Unable to publish this comment'),
            });
        }
    }
}
